import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Header } from './header';
import { CustomRequest, IDocument } from '../../../../../types';
import { CustomRequestDocumentsSponsorTypesEnum } from '../../../../../types/enums';
import NoDocuments from '../../../../atoms/no-doc-placeholder';
import { VisaVignetteDocs } from '../../../../molecules/followUpDocumentSection/beforeTravel/visaVignette';
import { NewStudentVisaVignetteModal } from '../../../modals/followUpDocs/newStudentVisaVignetteModal';
import { TravelTicketDocs } from '../../../../molecules/followUpDocumentSection/beforeTravel/travelTicket';
import { NewTravelTicketModal } from '../../../modals/followUpDocs/newTravelTicketModal';
import { VisaApplicationPdfCopyDocs } from '../../../../molecules/followUpDocumentSection/beforeTravel/visaApplicationPdfCopy';
import { NewVisaApplicationPdfCopyModal } from '../../../modals/followUpDocs/visaApplicationPdfCopyModal';
import { VisaVignetteDoc } from 'types/dist/followUpDocuments/shared';
import {
  ImmigrationStatusShareCodeDoc,
  SectionStatusType,
  StudentRefusalLetterDoc,
  TravelTicketDoc,
  VisaApplicationPdfCopyDoc
} from 'types';
import {
  ImmigrationStatusShareCodeModal,
  ImmigrationStatusPdfCopy,
  StudentRefusalLetterModal
} from 'shared-components';
import { ImmigrationStatusShareCodeDocs } from '../../../../molecules/followUpDocumentSection/beforeTravel/immigrationStatusShareCode';
import CustomRequestModal from '../../../modals/followUpDocs/customRequestModal';
import CustomRequestDocument from '../../../../molecules/followUpDocumentSection/customRequest';
import { StudentRefusalLetterDocs } from '../../../../molecules/followUpDocumentSection/beforeTravel/studentRefusalLetter';
import SectionHeader from '../../../../atoms/SectionHeader';
import { titleAppend } from '../../../../../utilities/head';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import { createNavigateToAction } from '../../../../../middleware/actions';
import * as uiActions from '../../../../../store/ui/actions';
import { getStudentFollowUpDocs, saveFollowUpDocumentsShareCode } from '../../../../../store/student/actions';
import { SectionId } from '../../../../molecules/Sidebar/types';
import { BiometricResidencePermitDoc } from '../../../../../types';
import { hasAnyMatchingFollowUpDocs } from '../../../../../utilities/documents';
import FileUploadLocal from '../../../modals/fileUpload/FileUploadLocal';

const PreDepartureDocuments = () => {
  const student = useSelector((state: ApplicationState) => state.student);
  const hasRequiredBeforeTravelDocs = hasAnyMatchingFollowUpDocs(student.followUpDocuments, 'beforeTravel', 'required');

  const beforeArrivalDocAreaStatus = useSelector((state: ApplicationState) => state.config.beforeArrivalDocAreaStatus);
  const followUpDocuments = useSelector((state: ApplicationState) => state.student.followUpDocuments);
  const summary = useSelector((state: ApplicationState) => state.student.summary);
  const config = useSelector((state: ApplicationState) => state.config.sections.FollowUpDocuments);
  const openModal = useSelector((state: ApplicationState) => state.ui.openModal);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getStudentFollowUpDocs());
  }, [dispatch]);

  const goToTravelStatusTab = () => {
    dispatch(uiActions.updateActiveSectionId(SectionId.ENROLMENT));
    dispatch(createNavigateToAction('/dashboard?isModalOpen=true'));
  };
  const saveShareCode = (docKey: string, data: any) => {
    dispatch(saveFollowUpDocumentsShareCode(docKey, data));
    dispatch(uiActions.closeModal());
  };
  const openUploadModal = (
    docKey: string,
    docType: string,
    sponsorType: string,
    sponsorKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metadata?: any
  ) => {
    dispatch(
      uiActions.openUploadFollowUpModal(sponsorType, sponsorKey, docType, docKey, modalTitle, fileNames, metadata)
    );
  };
  const openDocumentModal = (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string,
    docKey: string,
    modalTitle: string,
    fileNames: string[],
    isComplete: boolean,
    biometricResidencePermit?: BiometricResidencePermitDoc
  ) => {
    dispatch(
      uiActions.openFollowUpDocumentModal(
        sponsorType,
        sponsorKey,
        docType,
        docKey,
        modalTitle,
        fileNames,
        isComplete,
        biometricResidencePermit
      )
    );
  };
  const closeModal = () => {
    dispatch(uiActions.closeModal());
  };

  const getModal = () => {
    if (openModal?.isUpload) {
      return <FileUploadLocal openModal={openModal} />;
    }
    switch (openModal?.docType) {
      // BEFORE TRAVEL
      case 'studentRefusalLetter': {
        const doc: StudentRefusalLetterDoc = followUpDocuments.beforeTravel.studentRefusalLetter[openModal.docKey];
        const isComplete = ['UPLOADED_OK', 'APPROVED'].includes(doc.status);
        const {
          fileNames,
          refusalDate,
          applicationDate,
          attendedInterview,
          refusalGrounds,
          refusalGroundsInfo,
          appliedForAdminReview,
          adminReviewDate
        } = doc;

        return (
          <StudentRefusalLetterModal
            complete={isComplete}
            docKey={openModal.docKey}
            fileNames={fileNames ?? []}
            refusalDate={refusalDate}
            applicationDate={applicationDate}
            attendedInterview={attendedInterview}
            refusalGrounds={refusalGrounds}
            refusalGroundsInfo={refusalGroundsInfo}
            appliedForAdminReview={appliedForAdminReview}
            adminReviewDate={adminReviewDate}
            onClose={closeModal}
            onContinue={() => {
              openDocumentModal(
                'beforeTravel',
                null,
                'studentRefusalLetter',
                openModal.docKey,
                'Student Visa Refusal',
                doc.fileNames ?? [],
                isComplete,
                {}
              );
            }}
            onUpload={(
              sponsorType: string,
              sponsorKey: string | null,
              docType: string | null,
              docKey: string | null,
              modalTitle: string,
              fileNames: string[],
              metaData: any
            ) => {
              openUploadModal(docKey, docType, sponsorType, null, modalTitle, fileNames, metaData);
            }}
          />
        );
      }
      case 'visaVignette': {
        const doc: VisaVignetteDoc = followUpDocuments.beforeTravel.visaVignette[openModal.docKey];
        const { fileNames, visaStartDate, visaEndDate } = doc;
        return (
          <NewStudentVisaVignetteModal
            docType="visaVignette"
            sponsorType="beforeTravel"
            docKey={openModal.docKey}
            isComplete={openModal.isComplete}
            fileNames={fileNames ?? []}
            closeModal={closeModal}
            openUploadModal={openUploadModal}
            visaStartDate={visaStartDate}
            visaEndDate={visaEndDate}
          />
        );
      }
      case 'travelTicket': {
        const doc: TravelTicketDoc = followUpDocuments.beforeTravel.travelTicket[openModal.docKey];
        const { fileNames, flightNumber } = doc;
        return (
          <NewTravelTicketModal
            docType="travelTicket"
            sponsorType="beforeTravel"
            title={openModal.modalTitle}
            docKey={openModal.docKey}
            isComplete={openModal.isComplete}
            fileNames={fileNames ?? []}
            closeModal={closeModal}
            openUploadModal={openUploadModal}
            flightNumber={flightNumber}
          />
        );
      }
      case 'visaApplicationPdfCopy': {
        const doc: VisaApplicationPdfCopyDoc = followUpDocuments.beforeTravel.visaApplicationPdfCopy[openModal.docKey];
        const { fileNames, date } = doc;
        return (
          <NewVisaApplicationPdfCopyModal
            docType="visaApplicationPdfCopy"
            title={openModal.modalTitle}
            sponsorType="beforeTravel"
            docKey={openModal.docKey}
            isComplete={openModal.isComplete}
            fileNames={fileNames ?? []}
            closeModal={closeModal}
            openUploadModal={openUploadModal}
            date={date}
          />
        );
      }
      case 'immigrationStatusShareCode': {
        const doc: ImmigrationStatusShareCodeDoc =
          followUpDocuments.beforeTravel.immigrationStatusShareCode[openModal.docKey];
        return (
          <ImmigrationStatusShareCodeModal
            closeModal={closeModal}
            docKey={openModal.docKey}
            shareCode={doc}
            title={openModal.modalTitle}
            onSave={(docKey: string, data: any) => {
              saveShareCode(docKey, data);
            }}
          />
        );
      }
      case 'immigrationStatusPdfCopy': {
        const doc: any = followUpDocuments.beforeTravel?.immigrationStatusPdfCopy?.[openModal.docKey] ?? {};
        const isComplete = ['UPLOADED_OK', 'APPROVED'].includes(doc?.status);
        return (
          <ImmigrationStatusPdfCopy
            docKey={openModal.docKey}
            complete={isComplete}
            fileNames={doc.fileNames ?? []}
            onContinue={() => {
              openDocumentModal(
                'beforeTravel',
                null,
                'immigrationStatusPdfCopy',
                openModal.docKey,
                'Immigration Status: PDF',
                doc.fileNames ?? [],
                isComplete,
                {}
              );
            }}
            onUpload={(
              sponsorType: string,
              sponsorKey: string | null,
              docType: string | null,
              docKey: string | null,
              modalTitle: string,
              fileNames: string[]
            ) => {
              openUploadModal(docKey, docType, sponsorType, null, modalTitle, fileNames, null);
            }}
            onClose={closeModal}
          />
        );
      }
      // END BEFORE TRAVEL

      case 'customRequest': {
        const { sponsorType, sponsorKey, docKey } = openModal;
        const customReqDoc: CustomRequest =
          followUpDocuments[sponsorType as 'beforeTravel' | 'afterArrival'].customRequest[sponsorKey];
        const doc = customReqDoc[docKey];

        return (
          <CustomRequestModal
            sponsorKey={openModal.sponsorKey}
            complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
            fileNames={doc.fileNames}
            sectionTitle={'Custom Document'}
            label={doc.label ?? ''}
            description={doc.desc ?? ''}
            onContinue={closeModal}
            document={doc}
            openUploadModal={() =>
              openUploadModal(
                docKey,
                'customRequest',
                sponsorType,
                sponsorKey,
                'Custom request document',
                doc.fileNames
              )
            }
            onClose={closeModal}
          />
        );
      }
      default:
        return null;
    }
  };

  const pageTitle = 'Before Travel to the UK';

  const renderBeforeTravelDocs = () => {
    console.log('In Render Travel Docs');
    return (
      <>
        <SectionHeader
          title={pageTitle}
          subtext="Please upload the following documents as soon as possible."
          data-selector="before-arrival-area"
        />

        <StudentRefusalLetterDocs
          openDocumentModal={openDocumentModal}
          documents={followUpDocuments.beforeTravel.studentRefusalLetter}
        />

        <VisaVignetteDocs
          openDocumentModal={openDocumentModal}
          documents={followUpDocuments.beforeTravel.visaVignette}
        />

        <TravelTicketDocs
          openDocumentModal={openDocumentModal}
          documents={followUpDocuments.beforeTravel.travelTicket}
        />

        <VisaApplicationPdfCopyDocs
          openDocumentModal={openDocumentModal}
          documents={followUpDocuments.beforeTravel.visaApplicationPdfCopy}
        />

        <ImmigrationStatusShareCodeDocs
          openDocumentModal={openDocumentModal}
          shareCodeDocuments={followUpDocuments.beforeTravel.immigrationStatusShareCode}
          pdfCopyDocuments={followUpDocuments.beforeTravel.immigrationStatusPdfCopy}
        />

        <CustomRequestDocument
          sponsorType={CustomRequestDocumentsSponsorTypesEnum.BEFORE_TRAVEL}
          openDocumentModal={openDocumentModal}
          documents={followUpDocuments.beforeTravel.customRequest}
        />
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          {pageTitle} {titleAppend}
        </title>
      </Helmet>

      {getModal()}

      <Header config={config} goToTravelStatusTab={goToTravelStatusTab} />

      {summary.followUpDocsTotal > 0 && beforeArrivalDocAreaStatus === SectionStatusType.ACTIVE ? (
        <>{hasRequiredBeforeTravelDocs ? renderBeforeTravelDocs() : null}</>
      ) : (
        <NoDocuments />
      )}
    </>
  );
};

export default PreDepartureDocuments;
